import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import {
  type Dispatch,
  type SetStateAction,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Map as KakaoMap, MapMarker, MarkerClusterer } from 'react-kakao-maps-sdk';
import { Filter } from '@peacecover/icon';
import useArtistQuery from '../../components/api/queries/useArtistQuery';
import { useLocationsQuery } from '../../components/api/queries/useLocationsQuery';
// import useUserQuery from '../../components/api/queries/useUserQuery';
import OfflineLocationBottomSheet, {
  getPercentageFromPx
} from '../../components/bottomSheet/OfflineLocationBottomSheet';
import OfflineLocationFilterBottomDrawer from '../../components/drawer/OfflineLocationFilterBottomDrawer';
import EventMarkerContainer from '../../components/offlineLocation/EventMarkerContainer';
import MarkerProvider from '../../components/offlineLocation/MarkerProvider';
import OfflineLocationMenu from '../../components/offlineLocation/OfflineLocationMenuV2';
import { Location } from '../../domain/models/locations/Location';
import type { SimpleMember } from '../../domain/models/locations/SimpleMember';
import type { CoordinatesLocation } from '../../types/OfflineLocation';
import type { ILocationItemResponseBody } from '../../types/response/ILocationItemResponseBody';
import { openNewTab } from '../../utils/UIUtils';
import requestGeoLocation from '../../utils/requestGeoLocation';
import useKakaoLoader from '../../utils/useKakaoLoader';
import PlaceEventModal from './PlaceEventModal';
import { FabSheet } from '../../presentation/components/floating';
import OfflineLocationListContent from '../../components/offlineLocation/OfflineLocationListContent';
import EnLocaleWrapper from '../../presentation/components/EnLocaleWrapper';
import PriamryCheckBox from '../../components/checkbox/PriamryCheckBox';

const TOP_NAV_HEIGHT = 64;
const FAN_MATE_BAR_HEIGHT = 53;
const SUB_TAB_HEIGHT = 64;
const BOTTOM_NAV_HEIGHT = 65.5; // 실제 메뉴탭 높이(px)
const MODAL_BOTTOM_OFFSET = 19;
const DEFAULT_BOTTOMSHEET_HEIGHT = 140;

const location_icon_map = '/assets/icons/offlineLocation/location_icon_map.png';
const MarkerContext = createContext(null);

interface ISimpleArtist {
  artistId: string;
  fandomName: string;
}

interface IOfflineLocationTabProps {
  selectedArtist: ISimpleArtist;
}

const OfflineLocationTab: React.FC<IOfflineLocationTabProps> = ({ selectedArtist }) => {
  // Load Kakao Map
  useKakaoLoader();

  const { colorMode } = useColorMode();
  const center = { lat: 37.556944, lng: 126.923917 };
  // State
  const [positions, setPositions] = useState<Location[]>([]);
  const [mapLocation, setMapLocation] = useState<Omit<CoordinatesLocation, 'isLoading'>>({
    center,
    updateKey: 0
  });
  const [zoomLevel, setZoomLevel] = useState<number>(5);
  const [selectedSiteTypes, setSelectedSiteTypes] = useState<string[]>([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>();
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [onlyOngoingEvent, setOnlyOngoingEvent] = useState(false);
  const [isShowFilterBottomDrawer, setIsShowFilterBottomDrawer] = useState(false);
  const [isFilterButtonActive, setIsFilterButtonActive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bottomSheetHeightPx, setBottomSheetHeightPx] = useState<number>(DEFAULT_BOTTOMSHEET_HEIGHT);
  const [isBottomSheetDragging, setIsBottomSheetDragging] = useState<boolean>(false);
  const [userLocation, setUserLocation] = useState<CoordinatesLocation>({
    center,
    isLoading: true,
    updateKey: 0
  });

  // API
  // const user = useUserQuery();
  const artistId = selectedArtist.artistId;
  const artist = useArtistQuery(artistId);
  const { i18n } = useTranslation();
  const isEnLocale = i18n.language?.startsWith('en') ?? false;
  const mapHeight =
    window.innerHeight - TOP_NAV_HEIGHT - SUB_TAB_HEIGHT - BOTTOM_NAV_HEIGHT - FAN_MATE_BAR_HEIGHT;
  // 영어 리스트 뷰: CollectionHomePage 레이아웃에 맞춰 100dvh 기반으로 계산 (하단 공백 방지)
  const listViewHeight = isEnLocale
    ? 'calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 180px)'
    : mapHeight;

  const members = useMemo(() => {
    return (
      artist?.members?.map((member: SimpleMember) => ({
        memberId: member.memberId,
        name: member.name
      })) || []
    );
  }, [artist]);

  const { data: locations } = useLocationsQuery({
    latitude: mapLocation.center.lat,
    longitude: mapLocation.center.lng,
    zoomLevel: zoomLevel,
    artistId: artistId,
    memberIds: selectedMemberIds,
    siteTypes: selectedSiteTypes,
    status: onlyOngoingEvent ? 'ongoing' : undefined
  });

  useEffect(() => {
    // Initialize selected filter state
    if (selectedArtist) {
      setSelectedSiteTypes([]);
      setSelectedMemberIds(undefined);
    }
  }, [selectedArtist]);

  useEffect(() => {
    if (locations) {
      setPositions(
        locations.locations.map((location: ILocationItemResponseBody) =>
          Location.fromResponse(location)
        )
      );
    }
  }, [locations]);

  const handleMapUpdate = useCallback(
    debounce((map: kakao.maps.Map) => {
      const level = map.getLevel();
      const latlng = map.getCenter();
      setZoomLevel(level);
      setMapLocation((prev) => ({
        ...prev,
        center: {
          lat: latlng.getLat(),
          lng: latlng.getLng()
        }
      }));
    }, 750),
    []
  );

  useEffect(() => {
    if (selectedLocationId) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, [selectedLocationId]);

  const handleSiteTypeItemClick = (siteType: string) => {
    if (siteType === 'filter') {
      setIsShowFilterBottomDrawer(!isShowFilterBottomDrawer);
    } else {
      setSelectedSiteTypes((prev) =>
        prev.includes(siteType) ? prev.filter((item) => item !== siteType) : [...prev, siteType]
      );
    }
  };

  const handleChangePositionState = (pos: Location) => {
    setPositions((prev) => prev.filter((position) => position.locationId !== pos.locationId));
    setPositions((prev) => [pos, ...prev]);
    setSelectedLocationId(pos.locationId);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLocationId('');
  };

  // BottomSheet 이벤트 구독: 높이(px)와 드래그 상태만 수신
  useEffect(() => {
    const onHeight = (e: Event) => {
      const detail = (e as CustomEvent).detail as { heightPx: number };
      if (detail?.heightPx != null) setBottomSheetHeightPx(detail.heightPx);
    };
    const onDragging = (e: Event) => {
      const detail = (e as CustomEvent).detail as { isDragging: boolean };
      if (typeof detail?.isDragging === 'boolean') setIsBottomSheetDragging(detail.isDragging);
    };
    window.addEventListener('offlineBottomSheet:height', onHeight as EventListener);
    window.addEventListener('offlineBottomSheet:dragging', onDragging as EventListener);
    return () => {
      window.removeEventListener('offlineBottomSheet:height', onHeight as EventListener);
      window.removeEventListener('offlineBottomSheet:dragging', onDragging as EventListener);
    };
  }, []);

  const sortedPositions = useMemo(() => {
    if (!selectedLocationId) return positions;
    return [
      ...positions.filter((pos) => pos.locationId !== selectedLocationId),
      ...positions.filter((pos) => pos.locationId === selectedLocationId)
    ];
  }, [positions, selectedLocationId]);

  return (
    <div className="relative w-full">
      {/* Map Header Menus */}

      <EnLocaleWrapper inverted={true} fallback={null}>
        <div
          className="absolute w-full"
          style={{
            height: '76px',
            zIndex: '1',
            background:
              colorMode === 'dark'
                ? 'linear-gradient(to bottom, rgba(13, 14, 18, 1), rgba(13, 14, 18, 0))'
                : 'linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))'
          }}
        >
          <div className="absolute w-full" style={{ top: '4px' }}>
            <OfflineLocationMenu
              selectedMemberIds={selectedMemberIds || []}
              selectedMenuItemIds={selectedSiteTypes || []}
              onRequestNativeGeoLocation={() =>
                RequestNativeGeoLocation(setUserLocation, setMapLocation)
              }
              onItemClick={handleSiteTypeItemClick}
              isFilterButtonActive={isFilterButtonActive}
              setIsFilterButtonActive={setIsFilterButtonActive}
            />
          </div>
        </div>
      </EnLocaleWrapper>

      <div
        className={'relative w-full'}
        style={{ height: isEnLocale ? listViewHeight : mapHeight }}
      >
        <EnLocaleWrapper
          inverted={true}
          fallback={
            <div
              className="absolute w-full flex flex-col z-0 bg-background-light dark:bg-background-dark"
              style={{ height: listViewHeight }}
            >
              {/* 제목 + 필터 + 종료된 이벤트 제외 */}
              <div className="flex-shrink-0 px-[20px] pt-[16px] pb-[12px]">
                <h2 className="text-[17px] font-bold text-text-main-lv2 mb-[12px]">
                  {t('OfflineLocation.offlineLocationText', {
                    fandomName: selectedArtist?.fandomName || '-'
                  })}{' '}
                  <span className="text-primary-light dark:text-primary-dark">
                    {positions.length}
                  </span>
                </h2>
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-row items-center gap-[4px]">
                    <PriamryCheckBox
                      size="md"
                      isChecked={onlyOngoingEvent}
                      onChanged={setOnlyOngoingEvent}
                    />
                    <button
                      type="button"
                      className="text-[14px] font-semibold text-text-main-lv2 cursor-pointer bg-transparent border-0 p-0 text-left"
                      onClick={() => setOnlyOngoingEvent((prev) => !prev)}
                    >
                      {t('OfflineLocationFilterBottomDrawer.onlyOngoingEvents')}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="flex flex-row items-center gap-[4px] px-[12px] py-[6px] rounded-full border border-solid border-separator-light-1 dark:border-separator-dark-1 text-[13px] font-semibold text-main-light-2 dark:text-main-dark-2"
                    onClick={() => handleSiteTypeItemClick('filter')}
                  >
                    <Filter size={14} className="text-text-main-lv1" />
                    {t('SiteTypes.filter')}
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                <OfflineLocationListContent
                  positions={positions}
                  artistId={artistId}
                  variant="listView"
                />
              </div>
            </div>
          }
        >
          <KakaoMap
            center={mapLocation.center}
            className={'absolute w-full z-0'}
            style={{
              height: mapHeight,
              filter: colorMode === 'dark' ? 'invert(1) hue-rotate(180deg)' : ''
            }}
            level={zoomLevel}
            onDragEnd={handleMapUpdate}
            onZoomChanged={handleMapUpdate}
            key={mapLocation.updateKey}
          >
            <MarkerClusterer
              averageCenter={true}
              minLevel={6}
              styles={[
                {
                  width: '70px',
                  height: '70px',
                  background: 'rgba(92, 61, 250, .5)',
                  borderRadius: '70px',
                  color: '#fff',
                  textAlign: 'center',
                  lineHeight: '70px'
                }
              ]}
            >
              {!userLocation.isLoading && (
                <MapMarker
                  position={userLocation.center}
                  image={{
                    src: location_icon_map,
                    size: {
                      width: 22,
                      height: 22
                    }
                  }}
                />
              )}

              <MarkerProvider MarkerContext={MarkerContext}>
                {sortedPositions.map((pos: Location) => (
                  <EventMarkerContainer
                    MarkerContext={MarkerContext}
                    key={pos.locationId}
                    selectedLocationId={selectedLocationId}
                    pos={pos}
                    onChangePositionState={() => handleChangePositionState(pos)}
                    forceDarkMode={colorMode === 'dark'}
                  />
                ))}
              </MarkerProvider>
            </MarkerClusterer>
            {/* FabSheet: 바텀시트 상단 12px 위. 드래그 중 또는 최소 높이 아닐 때 숨김 */}
            {!isBottomSheetDragging && bottomSheetHeightPx <= 200 && (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-[calc(env(safe-area-inset-bottom)+100px)]">
                <FabSheet
                  onClick={() =>
                    openNewTab(
                      `/report/select-member?reportType=event&artistId=${artist?.artistId}`
                    )
                  }
                />
              </div>
            )}
          </KakaoMap>
        </EnLocaleWrapper>
        {/*
        <div
          className="absolute z-1 safeAreaMarginBottom"
          style={{
            bottom: `calc(env(safe-area-inset-top) + ${
              DEFAULT_BOTTOMSHEET_HEIGHT - BOTTOM_NAV_HEIGHT + 16
            }px)`,
            right: '18px'
          }}
          onMouseDown={() => RequestNativeGeoLocation(setUserLocation, setMapLocation)}
        >
          <div className="flex items-center justify-center rounded-[90px] gap-[4px] px-[12px] py-[8px] bg-main-light-2 dark:bg-main-dark-2">
            <PlaceIcon width={14} height={14} className="fill-pure-white dark:fill-pure-black" />
            <div className="text-[13px] font-medium text-pure-white dark:text-pure-black">
              내 위치
            </div>
          </div>
        </div>
        */}
      </div>

      <div>
        {/* 1. Selected Location Modal */}
        {isModalOpen && selectedLocationId ? (
          (() => {
            const pos = positions.find((pos) => pos.locationId === selectedLocationId);
            return pos ? (
              <PlaceEventModal
                key={pos.locationId}
                open={isModalOpen}
                onClose={handleCloseModal}
                pos={pos}
                style={{
                  bottom: `calc(${
                    BOTTOM_NAV_HEIGHT + MODAL_BOTTOM_OFFSET
                  }px + env(safe-area-inset-bottom, 0px))`,
                  zIndex: 2000
                }}
              />
            ) : (
              <></>
            );
          })()
        ) : isShowFilterBottomDrawer ? (
          // 2. Filter Bottom Drawer
          <OfflineLocationFilterBottomDrawer
            isOpen={isShowFilterBottomDrawer}
            members={members || []}
            initialSelectedMembers={selectedMemberIds}
            initialSelectedSiteTypes={selectedSiteTypes}
            initialOnlyOngoingEvent={onlyOngoingEvent}
            setIsFilterButtonActive={setIsFilterButtonActive}
            onApplyFilter={(onlyOngoingEvent, selectedMemberIds, selectedSiteTypes) => {
              setOnlyOngoingEvent(onlyOngoingEvent);
              setSelectedMemberIds(selectedMemberIds);
              setSelectedSiteTypes(selectedSiteTypes);
            }}
            onClose={() => setIsShowFilterBottomDrawer(false)}
          />
        ) : (
          // 3. Default Bottom Sheet (영어 로케일에서는 맵 영역에 리스트 표시하므로 숨김)
          <EnLocaleWrapper inverted={true} fallback={null}>
            <OfflineLocationBottomSheet
              selectedPinOrOption={selectedLocationId} // ''이면 항상 minHeight로만 보임
              headerTitleElement={
                <div>
                  {t('OfflineLocation.offlineLocationText', {
                    fandomName: selectedArtist?.fandomName || '-'
                  })}{' '}
                  <span className="text-primary-light dark:text-primary-dark">
                    {positions.length}
                  </span>
                </div>
              }
              minHeight={getPercentageFromPx(140)}
              midHeight={getPercentageFromPx(360)}
              fullHeight="90%"
            >
              <OfflineLocationListContent
                positions={positions}
                artistId={artistId}
                variant="bottomSheet"
              />
            </OfflineLocationBottomSheet>
          </EnLocaleWrapper>
        )}
      </div>
    </div>
  );
};

export default OfflineLocationTab;

const RequestNativeGeoLocation = async (
  setUserLocation: Dispatch<SetStateAction<CoordinatesLocation>>,
  setMapLocation?: Dispatch<SetStateAction<Omit<CoordinatesLocation, 'isLoading'>>>
) => {
  setUserLocation((prev) => ({
    ...prev,
    isLoading: true
  }));

  try {
    const newLocation = await requestGeoLocation();

    // 사용자 위치 업데이트
    setUserLocation((prev) => ({
      ...prev,
      center: newLocation,
      isLoading: false,
      updateKey: prev.updateKey + 1
    }));

    // 맵 위치도 함께 업데이트 (선택적)
    if (setMapLocation) {
      setMapLocation((prev) => ({
        ...prev,
        center: newLocation,
        updateKey: prev.updateKey + 1
      }));
    }
  } catch (_error) {
    setUserLocation((prev) => ({
      ...prev,
      isLoading: false
    }));
  }
};
